import { SpanTag } from "@tm/static";
import { theme } from "@tm/utility/global";
import { startOfDay } from "date-fns";
import { useEffect, useRef } from "react";
import { DateRange } from "react-day-picker";
import { emptyCellAfter, emptyCellBefore, getCalendarData } from "../../utils";
import {
  CalenderDate,
  CalenderDays,
  CalenderMonthName,
  CalenderWrap,
} from "./Calendar.style";

interface CalenderInterface {
  handleDayClick?: (day: Date) => void;
  startDate?: Date;
  endDate?: Date;
  monthNameType?:
    | "numeric"
    | "2-digit"
    | "long"
    | "short"
    | "narrow"
    | undefined;
  yearType?: "numeric" | "2-digit" | undefined;
  numberOfMonth?: number;
  selected?: DateRange | undefined;
  maxEndDate?: Date;
  minStartDate?: Date;
  isSingleDate?: boolean;
  onSingleDateSelect?: (selectedDate: Date) => void;
}

const Calendar = ({
  handleDayClick,
  startDate,
  endDate,
  monthNameType,
  yearType,
  selected,
  numberOfMonth = 13,
  maxEndDate,
  minStartDate,
  isSingleDate,
  onSingleDateSelect,
}: CalenderInterface) => {
  // const [sDate, setSDate] = useState(startDate);
  // const [eDate, setEDate] = useState(endDate);
  const monthRefs = useRef<(HTMLDivElement | null)[]>([]);
  const calendarData = getCalendarData(new Date(), maxEndDate);

  useEffect(() => {
    if (selected?.from) {
      const selectedMonth = selected.from.getMonth();
      const selectedYear = selected.from.getFullYear();
      const refIndex = calendarData?.findIndex(
        ({ month, year }) => month === selectedMonth && year === selectedYear
      );

      refIndex &&
        monthRefs.current[refIndex]?.scrollIntoView({
          behavior: "auto",
          block: "start",
        });
    }
  }, [selected, calendarData]);

  const disabledDays = (date: Date) => {
    return (
      isBefore(startOfDay(date), startOfDay(new Date())) ||
      !!(maxEndDate && isAfter(date, maxEndDate)) ||
      !!(minStartDate && isBefore(startOfDay(date), startOfDay(minStartDate)))
    );
  };

  const isBefore = (date1: Date, date2: Date) => date1 < date2;
  const isAfter = (date1: Date, date2: Date) => date1 > date2;

  return (
    <CalenderWrap>
      {calendarData?.map(
        ({ month, year, daysInMonth, firstDayOfWeek }, index) => {
          return (
            <div
              key={`${index}`}
              ref={(ref) => (monthRefs.current[index] = ref)}
            >
              <CalenderMonthName>
                {new Date(year, month).toLocaleString("en-us", {
                  month: monthNameType,
                })}{" "}
                &nbsp;
                {new Date(year, month).toLocaleString("en-us", {
                  year: yearType,
                })}
              </CalenderMonthName>
              <CalenderDays>
                {emptyCellBefore(firstDayOfWeek, daysInMonth)}
                {daysInMonth.days.map((day: any) => {
                  const d = new Date();
                  const curDate = d.getDate();
                  const curMonth = d.getMonth();
                  const curYear = d.getFullYear();

                  const isSelected = isSingleDate
                    ? day.date.getTime() ===
                      (selected?.from && selected?.from.getTime())
                    : day.date.getTime() ===
                        (selected?.from && selected?.from.getTime()) ||
                      day.date.getTime() ===
                        (selected?.to && selected?.to.getTime());

                  const isWithinRange =
                    !isSingleDate &&
                    selected &&
                    day.date > selected.from! &&
                    day.date < selected.to!;
                  const isDisabled = disabledDays(day.date);

                  return (
                    <CalenderDate
                      isSelected={isSelected}
                      isWithinRange={isWithinRange}
                      isDisabled={isDisabled}
                      onClick={
                        isSingleDate && onSingleDateSelect
                          ? () => onSingleDateSelect(day.date)
                          : handleDayClick
                          ? () => handleDayClick(day.date)
                          : () => {}
                      }
                      key={`${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}`}
                    >
                      <SpanTag
                        color={
                          isSelected
                            ? theme.whiteText
                            : isDisabled
                            ? theme.greyText7
                            : theme.blackText
                        }
                      >
                        {day.date.getDate()}
                      </SpanTag>
                    </CalenderDate>
                  );
                })}
                {emptyCellAfter(firstDayOfWeek, daysInMonth)}
              </CalenderDays>
            </div>
          );
        }
      )}
    </CalenderWrap>
  );
};

export default Calendar;
